import { Platform } from "react-native";
import { initializePlatformAuth } from "./firebaseAuth";
import { initializeFirebaseAppCheck } from "./firebaseAppCheck";
import { getApp, getApps, initializeApp } from "firebase/app";
import * as FirebaseAuth from "firebase/auth";
import { connectFirestoreEmulator, getFirestore, initializeFirestore } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { connectStorageEmulator, getStorage } from "firebase/storage";

const FIREBASE_EMULATOR_MODE =
  __DEV__ && process.env.EXPO_PUBLIC_USE_FIREBASE_EMULATORS === "true";
const FIREBASE_EMULATOR_HOST = "127.0.0.1";
const FIREBASE_EMULATOR_PORTS = {
  auth: 9099,
  firestore: 8080,
  functions: 5001,
  storage: 9199,
} as const;

const firebaseApiKey =
  process.env.EXPO_PUBLIC_FIREBASE_API_KEY ?? "AIzaSyBooly_PVguGibx0SKiz6ppWhbwbCTv2Zg";
const firebaseAuthDomain =
  process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN ?? "schoolme-e31ec.firebaseapp.com";
const firebaseProjectId =
  process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID ?? "schoolme-e31ec";
const firebaseStorageBucket =
  process.env.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET ?? "schoolme-e31ec.firebasestorage.app";
const firebaseMessagingSenderId =
  process.env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID ?? "578092338865";
const firebaseAppId =
  Platform.OS === "android"
    ? process.env.EXPO_PUBLIC_FIREBASE_ANDROID_APP_ID ?? "1:578092338865:android:6daeb9ad4678f6d2055c90"
    : process.env.EXPO_PUBLIC_FIREBASE_APP_ID ?? "1:578092338865:web:cb94dd2463bb4b9b055c90";
const firebaseMeasurementId =
  process.env.EXPO_PUBLIC_FIREBASE_MEASUREMENT_ID ?? "G-EFWT7DCGQ3";
const firebaseStorageUrl =
  process.env.EXPO_PUBLIC_FIREBASE_STORAGE_URL ?? "gs://schoolme-e31ec.firebasestorage.app";

const firebaseConfig = {
  apiKey: firebaseApiKey,
  authDomain: firebaseAuthDomain,
  projectId: firebaseProjectId,
  storageBucket: firebaseStorageBucket,
  messagingSenderId: firebaseMessagingSenderId,
  appId: firebaseAppId,
  measurementId: firebaseMeasurementId,
};

export const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

// App Check must initialize before any Firebase service is used.
// Web and Android resolve their own platform-specific providers.
initializeFirebaseAppCheck(app);

export const auth = initializePlatformAuth(app);
auth.languageCode = "fr";

export const db = (() => {
  try {
    // Firebase 12 auto-detects when long polling is required. Forcing it on
    // React Native can leave the WebChannel write stream pending indefinitely.
    return initializeFirestore(app, {});
  } catch {
    return getFirestore(app);
  }
})();

export const functionsClient = getFunctions(app);
export const storage = getStorage(
  app,
  firebaseStorageUrl
);

export const firebaseEmulatorMode = FIREBASE_EMULATOR_MODE;

if (FIREBASE_EMULATOR_MODE) {
  type FirebaseEmulatorConnectionState = {
    appName: string;
    configured: boolean;
  };

  const globalState = globalThis as typeof globalThis & {
    __classtiFirebaseEmulatorConnection?: FirebaseEmulatorConnectionState;
  };
  const connectionState = globalState.__classtiFirebaseEmulatorConnection;

  if (!connectionState || connectionState.appName !== app.name) {
    const endpoint = (service: keyof typeof FIREBASE_EMULATOR_PORTS) =>
      `${FIREBASE_EMULATOR_HOST}:${FIREBASE_EMULATOR_PORTS[service]}`;

    try {
      FirebaseAuth.connectAuthEmulator(auth, `http://${endpoint("auth")}`, {
        disableWarnings: true,
      });
      connectFirestoreEmulator(db, FIREBASE_EMULATOR_HOST, FIREBASE_EMULATOR_PORTS.firestore);
      connectFunctionsEmulator(
        functionsClient,
        FIREBASE_EMULATOR_HOST,
        FIREBASE_EMULATOR_PORTS.functions
      );
      connectStorageEmulator(storage, FIREBASE_EMULATOR_HOST, FIREBASE_EMULATOR_PORTS.storage);
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      throw new Error(
        `Firebase Emulator mode enabled, but the Emulator connection failed. ` +
          `No Firebase production fallback is allowed. ${reason}`
      );
    }

    globalState.__classtiFirebaseEmulatorConnection = {
      appName: app.name,
      configured: true,
    };
    console.info("Firebase Emulator mode enabled", {
      auth: endpoint("auth"),
      firestore: endpoint("firestore"),
      functions: endpoint("functions"),
      storage: endpoint("storage"),
    });
  }
}


